
import { useState } from "react";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, FileText } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { ApplicationFilters } from "@/components/lender/applications/ApplicationFilters";
import { ApplicationTabContent } from "@/components/lender/applications/ApplicationTabContent";
import { mockApplications } from "@/data/mockApplications";

const Applications = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const { data: applications, isLoading } = useQuery({
    queryKey: ['loan-applications'],
    queryFn: async () => {
      try {
        // In a real implementation, this would fetch data from Supabase
        // For demo purposes, we'll return mock data
        return mockApplications;
      } catch (error) {
        console.error('Error fetching applications:', error);
        toast({
          title: "Error",
          description: "Failed to load application data",
          variant: "destructive",
        });
        return [];
      }
    }
  });

  // Filter applications based on search term and active tab
  const filteredApplications = applications?.filter(app => {
    const matchesSearch = 
      app.borrower_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    if (activeTab === "pending") return matchesSearch && app.status === "pending";
    if (activeTab === "review") return matchesSearch && app.status === "review";
    if (activeTab === "approved") return matchesSearch && app.status === "approved";
    if (activeTab === "declined") return matchesSearch && app.status === "declined";
    
    return matchesSearch;
  }) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Loan Applications</h1>
        <FileText className="h-8 w-8 text-primary" />
      </div>
      
      <ApplicationFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <Card>
        <CardHeader className="pb-0">
          <CardTitle>
            <Tabs defaultValue="all" onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="review">Under Review</TabsTrigger>
                <TabsTrigger value="approved">Approved</TabsTrigger>
                <TabsTrigger value="declined">Declined</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardTitle>
        </CardHeader>
        <ApplicationTabContent applications={filteredApplications} />
      </Card>
    </div>
  );
};

export default Applications;
